import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, PencilSimple, FloppyDisk } from "@phosphor-icons/react";
import { api, errorMessage, inr } from "@/lib/api";
import { adminBtn, adminBtnGhost, adminInput } from "./AdminLayout";

function StockCell({ book }) {
  const qc = useQueryClient();
  const [val, setVal] = useState({ stock: book.stock ?? 0, hardcover_stock: book.hardcover_stock ?? 0 });
  const [busy, setBusy] = useState(false);
  const dirty = val.stock !== (book.stock ?? 0) || val.hardcover_stock !== (book.hardcover_stock ?? 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/books/${book.book_id}/stock`, { stock: Number(val.stock), hardcover_stock: Number(val.hardcover_stock) });
      qc.invalidateQueries({ queryKey: ["admin-books"] });
      qc.invalidateQueries({ queryKey: ["books"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success("Available count updated — the site shows it right away");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2">
      <label className="text-[11px] text-muted-foreground">PB
        <input type="number" min={0} value={val.stock} onChange={(e) => setVal({ ...val, stock: e.target.value })}
          className={`${adminInput} ml-1 w-16 py-1`} data-testid={`stock-pb-input-${book.book_id}`} />
      </label>
      <label className="text-[11px] text-muted-foreground">HC
        <input type="number" min={0} value={val.hardcover_stock} onChange={(e) => setVal({ ...val, hardcover_stock: e.target.value })}
          className={`${adminInput} ml-1 w-16 py-1`} data-testid={`stock-hc-input-${book.book_id}`} />
      </label>
      {dirty && (
        <button onClick={save} disabled={busy} className={adminBtnGhost} data-testid={`stock-save-${book.book_id}`}><FloppyDisk size={14} weight="bold" /> Save</button>
      )}
    </div>
  );
}

export default function AdminBooks() {
  const { data: books = [], isLoading } = useQuery({ queryKey: ["admin-books"], queryFn: async () => (await api.get("/admin/books")).data });
  return (
    <div data-testid="admin-books-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">Titles</h1>
          <p className="mt-1 text-sm text-muted-foreground">Available counts show live on the site and drop automatically with every paid order.</p>
        </div>
        <Link to="/admin/books/new" className={adminBtn} data-testid="add-book-button"><Plus weight="bold" /> Add title</Link>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Paperback</th><th className="px-4 py-3">Hardcover</th><th className="px-4 py-3">Available count</th><th className="px-4 py-3">Master PDF</th><th className="px-4 py-3">Sold</th><th className="px-4 py-3">Status</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {books.map((b) => (
              <tr key={b.book_id} className="border-t border-border/60" data-testid={`admin-book-row-${b.book_id}`}>
                <td className="px-4 py-3"><div className="flex items-center gap-3"><img src={b.cover_url} alt="" className="h-12 w-8 rounded object-cover" /><div><p className="font-medium">{b.title}</p><p className="text-xs text-muted-foreground">/{b.slug}</p></div></div></td>
                <td className="px-4 py-3">{b.physical_enabled ? inr(b.physical_price) : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-4 py-3">{b.hardcover_enabled ? inr(b.hardcover_price) : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-4 py-3"><StockCell book={b} /></td>
                <td className="px-4 py-3 text-xs">{b.pdf_path ? <span className="rounded-full bg-[#EEF1E4] px-2 py-0.5 font-semibold text-accent">Uploaded</span> : <span className="rounded-full bg-[#F9E3E0] px-2 py-0.5 font-semibold text-destructive">Missing</span>}</td>
                <td className="px-4 py-3">{b.sold_count || 0}</td>
                <td className="px-4 py-3 text-xs">{b.active ? "Live" : <span className="text-muted-foreground">Hidden</span>}</td>
                <td className="px-4 py-3 text-right"><Link to={`/admin/books/${b.book_id}`} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`edit-book-${b.book_id}`}><PencilSimple /> Edit</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
